import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type PracticeTrendExamName = 'Academic' | 'Core';
export type PracticeTrendCategory = 'Speaking' | 'Writing' | 'Reading' | 'Listening';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type PracticeTrendCounts = {
    attemptCount: number;
    practicedUsers: number;
    scoredUsers: number;
    zeroScoreUsers: number;
    above20Users: number;
    above40Users: number;
    above50Users: number;
    above60Users: number;
    above70Users: number;
    above80Users: number;
};

export type PracticeTrendInterval = {
    bucketStartUtc: string;
    bucketEndUtc: string;
    counts: PracticeTrendCounts;
};

export type PracticeTrendTaskType = {
    examTaskType: string;
    displayName: string;
    totals: PracticeTrendCounts;
    intervals: PracticeTrendInterval[];
};

export type PracticeTrendBucket = {
    bucketStartUtc: string;
    bucketEndUtc: string;
    label: string;
};

export type PracticeTrendsData = {
    generatedAtUtc: string;
    fromUtc: string;
    toUtc: string;
    intervalMinutes: number;
    examName: PracticeTrendExamName;
    category: PracticeTrendCategory;
    buckets: PracticeTrendBucket[];
    taskTypes: PracticeTrendTaskType[];
};

export type PracticeTrendsQuery = {
    examName: PracticeTrendExamName;
    category: PracticeTrendCategory;
    fromUtc: string;
    toUtc: string;
    intervalMinutes?: number;
};

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string) {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string) {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Practice trends request failed'));
    }

    return envelope.data ?? (payload as T);
}

async function requestAdmin<T>(path: string, query: Record<string, string>): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/${trimSlashes(path)}`);

    for (const [key, value] of Object.entries(query)) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers({ Accept: 'application/json' });
    if (adminToken) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), { method: 'GET', headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as T;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asNumber(value: unknown) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asString(value: unknown) {
    return typeof value === 'string' ? value : '';
}

function normalizeCounts(value: unknown): PracticeTrendCounts {
    const record = asRecord(value);
    return {
        attemptCount: asNumber(record.attemptCount),
        practicedUsers: asNumber(record.practicedUsers),
        scoredUsers: asNumber(record.scoredUsers),
        zeroScoreUsers: asNumber(record.zeroScoreUsers),
        above20Users: asNumber(record.above20Users),
        above40Users: asNumber(record.above40Users),
        above50Users: asNumber(record.above50Users),
        above60Users: asNumber(record.above60Users),
        above70Users: asNumber(record.above70Users),
        above80Users: asNumber(record.above80Users),
    };
}

function normalizeInterval(value: unknown): PracticeTrendInterval {
    const record = asRecord(value);
    return {
        bucketStartUtc: asString(record.bucketStartUtc),
        bucketEndUtc: asString(record.bucketEndUtc),
        counts: normalizeCounts(record.counts),
    };
}

function normalizePracticeTrends(value: unknown): PracticeTrendsData {
    const record = asRecord(value);
    return {
        generatedAtUtc: asString(record.generatedAtUtc),
        fromUtc: asString(record.fromUtc),
        toUtc: asString(record.toUtc),
        intervalMinutes: asNumber(record.intervalMinutes) || 30,
        examName: asString(record.examName) === 'Core' ? 'Core' : 'Academic',
        category: ['Writing', 'Reading', 'Listening'].includes(asString(record.category))
            ? (asString(record.category) as PracticeTrendCategory)
            : 'Speaking',
        buckets: Array.isArray(record.buckets)
            ? record.buckets.map((bucket) => {
                  const bucketRecord = asRecord(bucket);
                  return {
                      bucketStartUtc: asString(bucketRecord.bucketStartUtc),
                      bucketEndUtc: asString(bucketRecord.bucketEndUtc),
                      label: asString(bucketRecord.label),
                  };
              })
            : [],
        taskTypes: Array.isArray(record.taskTypes)
            ? record.taskTypes.map((taskType) => {
                  const taskRecord = asRecord(taskType);
                  return {
                      examTaskType: asString(taskRecord.examTaskType),
                      displayName: asString(taskRecord.displayName),
                      totals: normalizeCounts(taskRecord.totals),
                      intervals: Array.isArray(taskRecord.intervals)
                          ? taskRecord.intervals.map(normalizeInterval)
                          : [],
                  };
              })
            : [],
    };
}

export async function getPracticeTrends(query: PracticeTrendsQuery) {
    const response = unwrapAdminApiPayload(
        await requestAdmin<PracticeTrendsData | AdminApiEnvelope<PracticeTrendsData>>(
            'AdminPracticeTrends',
            {
                examName: query.examName,
                category: query.category,
                fromUtc: query.fromUtc,
                toUtc: query.toUtc,
                intervalMinutes: String(query.intervalMinutes ?? 30),
            },
        ),
    );

    return normalizePracticeTrends(response);
}
